import { useMutation, useQuery } from "@apollo/client/react";
import type { FragmentData } from "@gqlb/core";
import { AlertTriangle, ChevronDown, Clock, Flame, Minus, Plus, WheatOff, X } from "lucide-react";
import posthog from "posthog-js";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { b } from "@/b";
import { CurrencyRenderer } from "@/components/CurrencyRenderer";
import { MacrosNew } from "@/components/pages/product/MacrosNew";
import { NutritionLabelNew } from "@/components/pages/product/NutritionLabelNew";
import { OptionSelectorNew } from "@/components/pages/product/OptionSelectorNew";
import type { ProductCard_Fragment } from "@/components/pages/product/ProductCard";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { Spinner } from "@/components/Spinner";
import {
  flattenSelectionState,
  OPTION_SELECTOR_FRAGMENT,
  PRODUCT_ADD_TO_CART_MUTATION,
  PRODUCT_INFO_PROVISION_CART_ITEM_QUERY,
  type SelectionState,
} from "@/graphql/fragments";
import {
  FULFILLMENT_LABELS,
  type FulfillmentMode,
  firstEligibleMode,
  fulfillmentNoun,
} from "@/util/fulfillment";
import { klaviyoTrack } from "@/util/klaviyo";

// ---------------------------------------------------------------------------
// GraphQL — fragment for product detail data needed by the quick-add modal
// ---------------------------------------------------------------------------

export const QuickAddModal_Fragment = b.fragment("QuickAddModal", "Product", (b) => [
  b.description(),
  b.availableFulfillmentModes(),
  b.glutenFree(),
  b.spiceLevel(),
  b.tags((b) => [b.id(), b.name()]),
  b.availability((b) => [
    b.available(),
    b.availableUntil((b) => [
      b.distanceToNow({ suffix: false, strict: true }),
      b.formatted({ format: "MMMM do", onlyDay: true }),
    ]),
  ]),
  b.useFullNutritionLabel(),
  b.servingSize(),
  b.servingsPerContainer(),
  b.options((b) => [
    b.id(),
    b.maxSelections(),
    b.minSelections(),
    b.__fragment(OPTION_SELECTOR_FRAGMENT),
  ]),
]);

const VIP_ADD_TO_CART_MUTATION = b.mutation("QuickAdd_AddVIPToCart", {}, (b) => [
  b.viewer((b) => [
    b.cart((b) => [
      b.addSubscriptionItem({ slug: "vip" }, (b) => [b.id(), b.cart((b) => [b.totalQuantity()])]),
    ]),
  ]),
]);

// ---------------------------------------------------------------------------
// Helper — get default selections for a product
// ---------------------------------------------------------------------------

function getDefaultSelectionsFromOptions(
  options: ReadonlyArray<{
    readonly id: string;
    readonly minSelections: number | null;
    readonly maxSelections: number | null;
    readonly selections: ReadonlyArray<{ readonly id: string }>;
  }>,
): SelectionState {
  const selections: SelectionState = {};
  for (const option of options) {
    if (
      option.minSelections === 1 &&
      option.maxSelections === 1 &&
      option.selections.length === 1
    ) {
      selections[option.id] = {
        selections: new Set([option.selections[0].id]),
        subOptions: {},
        order: [],
      };
    }
  }
  return selections;
}

// ---------------------------------------------------------------------------
// Collapsible detail section
// ---------------------------------------------------------------------------

const _DetailSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        {title}
        <ChevronDown
          className={`size-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// QuickAddModal
// ---------------------------------------------------------------------------

export const QuickAddModal: React.FC<{
  product: FragmentData<typeof ProductCard_Fragment> & FragmentData<typeof QuickAddModal_Fragment>;
  callout?: string;
  isSubscribedToVIP: boolean;
  /** The menu's current fulfillment mode; a deep-linked meal may not be available for it. */
  fulfillmentMode?: FulfillmentMode;
  availableModes?: ReadonlyArray<FulfillmentMode>;
  onSwitchMode?: (mode: FulfillmentMode) => void;
  onClose: () => void;
  onAddedToCart?: (info: { imageUrl: string; rect: DOMRect }) => void;
}> = ({
  product: cardData,
  callout,
  isSubscribedToVIP,
  fulfillmentMode,
  availableModes,
  onSwitchMode,
  onClose,
  onAddedToCart,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  // Selection state — initialize from product options data
  const [selections, setSelections] = useState<SelectionState>(() =>
    getDefaultSelectionsFromOptions(cardData.options),
  );

  // Quantity
  const [quantity, setQuantity] = useState(1);

  // Provision query — real-time macros & pricing
  const provisionQuery = useQuery(PRODUCT_INFO_PROVISION_CART_ITEM_QUERY.document(), {
    variables: {
      productId: cardData.id,
      quantity,
      selections: flattenSelectionState(selections),
    },
    fetchPolicy: "no-cache",
  });

  const provisionalItem =
    provisionQuery.data?.viewer.cart.provisionProductItem ??
    provisionQuery.previousData?.viewer.cart.provisionProductItem;

  const _subtotal = provisionalItem?.unitPrice;
  const total = provisionalItem?.subtotal;
  const vipDiscount = provisionalItem?.vipDiscount?.amount;

  // Add to cart mutations — refetchQueries ensures the menu page's cart data updates
  const [mutateAddToCart, addToCartMutation] = useMutation(
    PRODUCT_ADD_TO_CART_MUTATION.document(),
    { refetchQueries: ["Menu"], awaitRefetchQueries: true },
  );
  const [mutateAddVIP] = useMutation(VIP_ADD_TO_CART_MUTATION.document(), {
    refetchQueries: ["Menu"],
  });

  const captureAndClose = useCallback(() => {
    const imgs = cardData.images;
    const currentImage = imgs[activeImageIndexRef.current] ?? imgs[0];
    const imageUrl = currentImage
      ? currentImage.url.startsWith("https://")
        ? currentImage.url
        : `/image/${currentImage.id}?size=400&q=75`
      : "";
    // Get the rect from the mosaic's main image area (first child of the mosaic container)
    const mosaicEl = imageScrollRef.current;
    const mainImageEl = mosaicEl?.querySelector("[data-main-image]") as HTMLElement | undefined;
    const rect =
      mainImageEl?.getBoundingClientRect() ??
      mosaicEl?.getBoundingClientRect() ??
      new DOMRect(window.innerWidth / 2 - 40, window.innerHeight / 2 - 40, 80, 80);
    onClose();
    if (imageUrl) onAddedToCart?.({ imageUrl, rect });
  }, [cardData.images, onClose, onAddedToCart]);

  // Whether options exist that need selecting and none have been selected yet
  const hasUnselectedOptions = useMemo(() => {
    return cardData.options.some((option) => {
      if (option.minSelections === null || option.minSelections <= 0) return false;
      const sel = selections[option.id];
      const count = sel ? sel.selections.size + Object.keys(sel.subOptions).length : 0;
      return count < option.minSelections;
    });
  }, [cardData.options, selections]);

  // Client-side option validation
  const validateSelections = useCallback((): boolean => {
    const unsatisfied: string[] = [];
    for (const option of cardData.options) {
      if (option.minSelections !== null && option.minSelections > 0) {
        const sel = selections[option.id];
        const count = sel ? sel.selections.size + Object.keys(sel.subOptions).length : 0;
        if (count < option.minSelections) {
          unsatisfied.push(option.id);
        }
      }
    }
    if (unsatisfied.length === 0) return true;

    // Scroll to center the options in the scrollable area
    const container = scrollContentRef.current;
    const target = optionsContainerRef.current;
    if (container && target) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = targetRect.top - containerRect.top + container.scrollTop;
      const scrollOffset = targetTop - (containerRect.height - targetRect.height) / 2;
      container.scrollTo({ top: Math.max(0, scrollOffset), behavior: "smooth" });
    }

    // Shake unsatisfied options via Web Animations API
    const shakeKeyframes: Keyframe[] = [
      { transform: "translateX(0)" },
      { transform: "translateX(-12px)" },
      { transform: "translateX(10px)" },
      { transform: "translateX(-8px)" },
      { transform: "translateX(8px)" },
      { transform: "translateX(-4px)" },
      { transform: "translateX(4px)" },
      { transform: "translateX(0)" },
    ];
    for (const id of unsatisfied) {
      const el = optionsContainerRef.current?.querySelector(`[data-option-id="${id}"]`);
      if (el instanceof HTMLElement) {
        el.animate(shakeKeyframes, { duration: 500, easing: "ease-in-out" });
      }
    }

    return false;
  }, [cardData.options, selections]);

  const handleAddToCart = useCallback(async () => {
    if (!validateSelections()) return;
    try {
      await mutateAddToCart({
        variables: {
          productId: cardData.id,
          quantity,
          selections: flattenSelectionState(selections),
        },
      });
      posthog.capture("product_added_to_cart", {
        product_id: cardData.id,
        product_name: cardData.name,
        quantity,
        price: cardData.viewerPrice.value,
      });
      klaviyoTrack("Added to Cart", {
        ProductID: cardData.id,
        ProductName: cardData.name,
        Quantity: quantity,
        Price: cardData.viewerPrice.value,
      });
      captureAndClose();
    } catch (_e) {}
  }, [
    cardData.id,
    cardData.name,
    cardData.viewerPrice.value,
    quantity,
    selections,
    mutateAddToCart,
    captureAndClose,
    validateSelections,
  ]);

  const handleAddWithVIP = useCallback(async () => {
    if (!validateSelections()) return;
    try {
      await mutateAddToCart({
        variables: {
          productId: cardData.id,
          quantity,
          selections: flattenSelectionState(selections),
        },
      });
      posthog.capture("product_added_to_cart", {
        product_id: cardData.id,
        product_name: cardData.name,
        quantity,
        price: cardData.viewerPrice.value,
        with_vip: true,
      });
      klaviyoTrack("Added to Cart", {
        ProductID: cardData.id,
        ProductName: cardData.name,
        Quantity: quantity,
        Price: cardData.viewerPrice.value,
        WithVIP: true,
      });
      await mutateAddVIP().catch(() => {});
      captureAndClose();
    } catch (_e) {}
  }, [
    cardData.id,
    cardData.name,
    cardData.viewerPrice.value,
    quantity,
    selections,
    mutateAddToCart,
    mutateAddVIP,
    captureAndClose,
    validateSelections,
  ]);

  // Klaviyo "Viewed Product" — the quick-add popup is the product view moment.
  // ProductID uses the catalog external_id convention (Sanity _id = cardData.id).
  // Guarded/no-op when the onsite script is absent. Fires once per product open.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per product view
  useEffect(() => {
    klaviyoTrack("Viewed Product", {
      ProductID: cardData.id,
      ProductName: cardData.name,
      Price: cardData.viewerPrice.value,
    });
  }, [cardData.id]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Close on overlay click (but not on panel click)
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const isUnavailable = !cardData.availability.available;
  // The menu hides meals not offered for the selected mode; a deep link can
  // still open one. Offer the switch instead of a dead end.
  const ineligibleForMode =
    !!fulfillmentMode && !cardData.availableFulfillmentModes.includes(fulfillmentMode);
  const switchTo =
    ineligibleForMode && onSwitchMode
      ? firstEligibleMode(cardData.availableFulfillmentModes, availableModes ?? [])
      : null;
  const cannotAdd = isUnavailable || ineligibleForMode;

  // Track if options section bottom is visible (initially or after scroll)
  const optionsEndRef = useRef<HTMLDivElement>(null);
  const [hasSeenOptions, setHasSeenOptions] = useState(true);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const checkOptionsVisible = useCallback(() => {
    const container = scrollContentRef.current;
    const marker = optionsEndRef.current;
    if (!container || !marker) return;
    const containerRect = container.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();
    if (markerRect.top <= containerRect.bottom) {
      setHasSeenOptions(true);
      setShowScrollHint(false);
    }
  }, []);

  const handleContentScroll = checkOptionsVisible;

  // When options mount, check if they're below the fold — only then show the hint
  const optionsEndCallbackRef = useCallback((el: HTMLDivElement | null) => {
    (optionsEndRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (el) {
      requestAnimationFrame(() => {
        const container = scrollContentRef.current;
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const markerRect = el.getBoundingClientRect();
        if (markerRect.top > containerRect.bottom) {
          // Options are below the fold — show hint
          setHasSeenOptions(false);
          setShowScrollHint(true);
        }
      });
    }
  }, []);
  const isAdding = addToCartMutation.loading;
  const images = cardData.images;
  const hasImage = images.length > 0;
  const hasMultipleImages = images.length > 1;

  // Image carousel state — start on index 1 if available (index 0 is the card image they already saw)
  const startIndex = images.length > 1 ? 1 : 0;
  const [activeImageIndex, setActiveImageIndex] = useState(startIndex);
  const activeImageIndexRef = useRef(startIndex);
  const imageScrollRef = useRef<HTMLDivElement>(null);
  const autoScrollPaused = useRef(false);

  const scrollToImage = useCallback((index: number) => {
    setActiveImageIndex(index);
    activeImageIndexRef.current = index;
  }, []);

  // Auto-rotate images every 5 seconds, pause on user interaction
  useEffect(() => {
    if (!hasMultipleImages) return;
    const interval = setInterval(() => {
      if (autoScrollPaused.current) return;
      setActiveImageIndex((prev) => {
        const next = (prev + 1) % images.length;
        activeImageIndexRef.current = next;
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [hasMultipleImages, images.length]);

  const handleUserImageInteraction = useCallback(
    (index: number) => {
      autoScrollPaused.current = true;
      scrollToImage(index);
    },
    [scrollToImage],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center animate-overlay-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal positioning */}
      {/* biome-ignore lint: click handler for overlay dismiss */}
      <div
        className="relative z-10 flex items-end lg:items-center justify-center w-full h-full"
        onClick={handleOverlayClick}
      >
        {/* Panel */}
        <div
          ref={panelRef}
          className="relative w-full lg:max-w-2xl max-h-[90vh] bg-white rounded-t-2xl lg:rounded-2xl overflow-hidden flex flex-col shadow-2xl animate-slide-up lg:animate-modal-in"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-20 flex items-center justify-center size-8 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors cursor-pointer"
          >
            <X className="size-4" />
          </button>

          {/* Scrollable content */}
          <div
            ref={scrollContentRef}
            onScroll={handleContentScroll}
            className="flex-1 overflow-y-auto overscroll-contain relative"
          >
            {/* Image mosaic — main image left 2/3, stacked thumbnails right 1/3 */}
            {hasImage && (
              <div ref={imageScrollRef} className="relative bg-gray-100">
                {hasMultipleImages ? (
                  <div className="flex h-72">
                    {/* Main image — 3/4 width, crossfades */}
                    <div data-main-image className="relative w-3/4 overflow-hidden">
                      {images.map((img, i) => (
                        <div
                          key={img.id}
                          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                          style={{ opacity: i === activeImageIndex ? 1 : 0 }}
                        >
                          <ResponsiveImage
                            image={img}
                            objectFit="cover"
                            className="absolute inset-0 w-full h-full"
                          />
                        </div>
                      ))}
                      {/* Callout badge */}
                      {callout && (
                        <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-brand-mint px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-black">
                          {callout}
                        </span>
                      )}
                    </div>
                    {/* Thumbnail stack — 1/3 width */}
                    <div className="w-1/4 flex flex-col gap-0.5 pl-0.5">
                      {images.slice(0, 3).map((img, i) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() => handleUserImageInteraction(i)}
                          className={`relative flex-1 overflow-hidden cursor-pointer transition-all duration-200 ${
                            i === activeImageIndex
                              ? "ring-2 ring-inset ring-brand-mint"
                              : "opacity-70 hover:opacity-100"
                          }`}
                        >
                          <ResponsiveImage
                            image={img}
                            objectFit="cover"
                            className="absolute inset-0 w-full h-full"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Single image — full width, compact */
                  <div data-main-image className="relative h-72">
                    <ResponsiveImage
                      image={images[0]}
                      objectFit="cover"
                      className="absolute inset-0 w-full h-full"
                    />
                    {callout && (
                      <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-brand-mint px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-black">
                        {callout}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Header: name + price */}
            <div className="px-5 pt-5">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold text-brand-black leading-snug">{cardData.name}</h2>
                {cardData.viewerPrice.value > 0 && (
                  <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                    {cardData.viewerPrice.value !== cardData.price.value && (
                      <span className="text-sm text-gray-400 line-through">
                        <CurrencyRenderer data={cardData.price} />
                      </span>
                    )}
                    <span
                      className={`text-base font-semibold ${cardData.viewerPrice.value !== cardData.price.value ? "text-brand-mint" : "text-brand-black"}`}
                    >
                      <CurrencyRenderer data={cardData.viewerPrice} />
                    </span>
                  </div>
                )}
              </div>

              {/* Dietary icons */}
              <div className="flex items-center gap-2 mt-2">
                {cardData.glutenFree && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                    <WheatOff className="size-3" />
                    GF
                  </span>
                )}
                {cardData.spiceLevel !== "NONE" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                    <Flame className="size-3" />
                    {cardData.spiceLevel === "MILD"
                      ? "Mild"
                      : cardData.spiceLevel === "MEDIUM"
                        ? "Medium"
                        : "Hot"}
                  </span>
                )}
                {cardData.tags.some((t) => t.name === "Dairy-Free") && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600">
                    Dairy-Free
                  </span>
                )}
              </div>

              {/* Brief description */}
              {cardData.description && (
                <p className="mt-2 text-sm text-gray-500 whitespace-pre-line">
                  {cardData.description}
                </p>
              )}

              {/* Not offered for the selected fulfillment mode */}
              {ineligibleForMode && fulfillmentMode && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <AlertTriangle className="size-4 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-800 flex-1">
                    Not available for {fulfillmentNoun(fulfillmentMode)}
                  </p>
                  {switchTo && (
                    <button
                      type="button"
                      onClick={() => onSwitchMode?.(switchTo)}
                      className="text-xs font-semibold text-brand-black underline cursor-pointer whitespace-nowrap"
                    >
                      Switch to {FULFILLMENT_LABELS[switchTo]}
                    </button>
                  )}
                </div>
              )}

              {/* Limited time alert */}
              {cardData.availability.availableUntil && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <Clock className="size-4 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-800">
                    <span className="font-semibold">Limited Time</span> — only available for{" "}
                    <span className="font-semibold">
                      {cardData.availability.availableUntil.distanceToNow.split(" ")[0]} more{" "}
                      {cardData.availability.availableUntil.distanceToNow.split(" ")[1]}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Options */}
            {cardData.options.length > 0 && (
              <div ref={optionsContainerRef} className="px-5 mt-6">
                <div className="h-px bg-gray-100 mb-6" />
                <div className="flex flex-col gap-4">
                  {cardData.options.map((option) => (
                    <OptionSelectorNew
                      showTitle
                      data={option}
                      productId={cardData.id}
                      onSelectionUpdate={(selectionUpdateFn) => {
                        setSelections((prev) => {
                          const newOptionState = selectionUpdateFn(
                            prev[option.id] ?? {
                              selections: new Set(),
                              subOptions: {},
                              order: [],
                            },
                          );
                          if (
                            newOptionState.selections.size === 0 &&
                            Object.keys(newOptionState.subOptions).length === 0
                          ) {
                            const { [option.id]: _, ...rest } = prev;
                            return rest;
                          }
                          return {
                            ...prev,
                            [option.id]: selectionUpdateFn(
                              prev[option.id] ?? {
                                selections: new Set(),
                                subOptions: {},
                                order: [],
                              },
                            ),
                          };
                        });
                      }}
                      selections={
                        selections[option.id] ?? {
                          selections: new Set(),
                          subOptions: {},
                        }
                      }
                      key={option.id}
                    />
                  ))}
                </div>
                <div ref={optionsEndCallbackRef} />
              </div>
            )}

            {/* Ingredients + detail sections */}
            {provisionalItem && (
              <div className="px-5 mt-6 pb-6 flex flex-col gap-6">
                {/* {provisionalItem.ingredientSummary && (
                  <>
                    <div className="h-px bg-gray-100" />
                    <IngredientList ingredients={provisionalItem.ingredientSummary} />
                  </>
                )} */}

                {cardData.useFullNutritionLabel && provisionalItem.macros && (
                  <>
                    <div className="h-px bg-gray-100" />
                    <div className="relative">
                      <div
                        className={
                          hasUnselectedOptions ? "blur-[6px] pointer-events-none select-none" : ""
                        }
                      >
                        <NutritionLabelNew
                          data={provisionalItem.macros}
                          ingredients={provisionalItem.ingredientSummary ?? ""}
                          servingSize={cardData.servingSize}
                          servingsPerContainer={
                            provisionalItem.calculatedServingsPerContainer ??
                            cardData.servingsPerContainer
                          }
                          totalGrams={provisionalItem.totalGrams}
                        />
                      </div>
                      {hasUnselectedOptions && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-500">
                            Select options to see nutrition
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Scroll hint — fades out once user has scrolled past options */}
          {showScrollHint && !hasSeenOptions && (
            <div className="relative z-10 pointer-events-none">
              <div
                className="h-10 -mt-10"
                style={{ background: "linear-gradient(to top, white 10%, transparent 100%)" }}
              />
              <div className="flex items-center justify-center gap-1.5 bg-white pb-1.5 -mt-px">
                <span className="text-sm font-medium text-gray-400">Scroll for Options</span>
                <ChevronDown className="size-4 text-gray-300" />
              </div>
            </div>
          )}

          {/* Sticky footer */}
          <div className="border-t border-gray-100 bg-white px-4 py-3 pb-[52px] lg:pb-3 space-y-2">
            {/* Macros + Quantity + Price in one compact section */}
            {provisionalItem?.macros && (
              <div className="relative">
                <div
                  className={
                    hasUnselectedOptions ? "blur-[6px] pointer-events-none select-none" : ""
                  }
                >
                  <MacrosNew data={provisionalItem.macros} />
                </div>
                {hasUnselectedOptions && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-500">
                      Select options to see macros
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="flex items-center justify-center size-7 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Minus className="size-3" />
                </button>
                <span className="text-sm font-semibold text-brand-black w-5 text-center select-none">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="flex items-center justify-center size-7 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Plus className="size-3" />
                </button>
              </div>
              <div className="text-right">
                {total ? (
                  <span className="text-base font-bold text-brand-black">
                    <CurrencyRenderer data={total} freeStyle="free" />
                  </span>
                ) : (
                  <Spinner size="sm" />
                )}
              </div>
            </div>

            {/* Discount breakdown */}
            {provisionalItem && provisionalItem.discounts.length > 0 && (
              <div className="space-y-1">
                {provisionalItem.discounts.map((discount) => (
                  <div
                    key={discount.description}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-green-600">{discount.description}</span>
                    <span className="text-green-600 font-medium">
                      -<CurrencyRenderer data={discount.amount} freeStyle="show" />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Action buttons — single row with optional VIP */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all cursor-pointer active:scale-[0.98]"
              >
                Back
              </button>
              {!isSubscribedToVIP && vipDiscount && !cannotAdd && (
                <button
                  type="button"
                  onClick={handleAddWithVIP}
                  disabled={isAdding}
                  className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold bg-brand-mint/20 text-brand-black hover:bg-brand-mint/30 transition-all cursor-pointer active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  VIP & save <CurrencyRenderer data={vipDiscount} freeStyle="show" />
                </button>
              )}
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={cannotAdd || isAdding}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer disabled:cursor-not-allowed ${
                  cannotAdd
                    ? "bg-gray-200 text-gray-400"
                    : "bg-brand-mint text-brand-black hover:bg-brand-mint/90 active:scale-[0.98]"
                }`}
              >
                {isAdding ? (
                  <Spinner size="sm" />
                ) : isUnavailable ? (
                  "Unavailable"
                ) : ineligibleForMode && fulfillmentMode ? (
                  `Not available for ${FULFILLMENT_LABELS[fulfillmentMode]}`
                ) : (
                  "Add to Cart"
                )}
              </button>
            </div>

            {/* Error display */}
            {addToCartMutation.error && (
              <p className="text-sm text-red-500 text-center">{addToCartMutation.error.message}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
